
import { useRef, useEffect } from 'react';
import { GoogleMapsListenerHandle } from './types';

interface UseAutocompleteInitializationProps {
  isLoaded: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onPlaceChanged: (place: google.maps.places.PlaceResult) => void;
}

export const useAutocompleteInitialization = ({
  isLoaded,
  inputRef,
  onPlaceChanged
}: UseAutocompleteInitializationProps) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const listenerRef = useRef<GoogleMapsListenerHandle | null>(null);

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google) return;

    try {
      // Clean up previous listener if it exists
      if (listenerRef.current) {
        listenerRef.current.remove();
        listenerRef.current = null;
      }

      // Clean up previous autocomplete instance
      autocompleteRef.current = null;

      // Create new autocomplete instance with correct types
      const options: google.maps.places.AutocompleteOptions = {
        types: ['address'],
      };
      
      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      
      // Add place_changed listener and store the result for cleanup
      const listener = autocompleteRef.current.addListener('place_changed', () => {
        if (autocompleteRef.current) {
          const selectedPlace = autocompleteRef.current.getPlace();
          onPlaceChanged(selectedPlace);
        }
      });

      // Store the listener for cleanup
      listenerRef.current = {
        remove: () => {
          if (listener) {
            google.maps.event.removeListener(listener);
          }
        }
      };
    } catch (err) {
      console.error('Error initializing autocomplete:', err);
    }
  };

  // Initialize autocomplete when Google Maps is loaded
  useEffect(() => {
    if (isLoaded) {
      // Wait for the DOM to update before initializing
      setTimeout(() => {
        initializeAutocomplete();
      }, 100);
    }

    return () => {
      // Clean up listener on unmount
      if (listenerRef.current) {
        listenerRef.current.remove();
      }
    };
  }, [isLoaded]);

  return {
    initializeAutocomplete
  };
};
